import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { getVideoById, getRelatedVideos } from '@/mocks/videos';
import { categoryColorMap } from '@/mocks/categories';
import type { Video } from '@/mocks/videos';

function ArticleSchema({ video }: { video: Video }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    url: `https://17c-moc.com/article/${video.id}`,
    datePublished: video.publishDate,
    author: {
      '@type': 'Organization',
      name: '17c·moc起草糖心在线看',
    },
    publisher: {
      '@type': 'Organization',
      name: '17c·moc起草糖心在线看',
    },
    image: video.thumbnail,
    about: {
      '@type': 'Thing',
      name: video.categoryName,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const [video, setVideo] = useState<Video | null>(null);
  const [relatedVideos, setRelatedVideos] = useState<Video[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (id) {
      const found = getVideoById(id);
      setVideo(found || null);
      if (found) {
        setRelatedVideos(getRelatedVideos(found).slice(0, 4));
      }
    }
  }, [id]);

  if (!video) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14">
          <div className="container-custom text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 bg-background-200 rounded-full flex items-center justify-center">
              <i className="ri-error-warning-line text-foreground-400 text-3xl" style={{ width: '32px', height: '32px' }}></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">视频未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的视频不存在或已被移除，请返回首页浏览其他内容。</p>
            <Link
              to="/"
              className="inline-flex px-6 py-3 bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const colorStyle = categoryColorMap[video.categoryColor] || categoryColorMap.primary;

  return (
    <>
      <ArticleSchema video={video} />
      <Navbar />

      <main className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: video.categoryName, href: `/category/${video.categorySlug}` },
            { label: video.title },
          ]}
        />

        <article className="py-8">
          <div className="container-custom max-w-4xl">
            <div className="relative aspect-video rounded-xl overflow-hidden mb-8 bg-foreground-900">
              <img
                src={video.thumbnail}
                alt={video.title}
                title={`17c·moc起草糖心在线看 - ${video.title}`}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center">
                <div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors">
                  <i className="ri-play-fill text-primary-500 text-3xl" style={{ width: '36px', height: '36px' }}></i>
                </div>
              </div>
              <span className="absolute bottom-4 right-4 bg-black/75 text-white text-sm px-3 py-1 rounded">
                {video.duration}
              </span>
            </div>

            <header className="mb-6">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <span className={`${colorStyle.bg} ${colorStyle.text} text-sm px-3 py-1 rounded-full font-medium`}>
                  <i className="ri-folder-line mr-1" style={{ width: '12px', height: '12px' }}></i>
                  {video.categoryName}
                </span>
                {video.isHot && (
                  <span className="bg-primary-100 text-primary-700 text-sm px-3 py-1 rounded-full font-medium">热门</span>
                )}
                {video.isNew && (
                  <span className="bg-accent-100 text-accent-700 text-sm px-3 py-1 rounded-full font-medium">最新</span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {video.title}
              </h1>
              <div className="flex items-center gap-4 md:gap-6 text-sm text-foreground-500 flex-wrap">
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line" style={{ width: '14px', height: '14px' }}></i>
                  </div>
                  {video.views > 10000 ? `${(video.views / 10000).toFixed(1)}万次观看` : `${video.views}次观看`}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-accent-500" style={{ width: '14px', height: '14px' }}></i>
                  </div>
                  {video.rating} 分
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line" style={{ width: '14px', height: '14px' }}></i>
                  </div>
                  {video.publishDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" style={{ width: '14px', height: '14px' }}></i>
                  </div>
                  {video.duration}
                </span>
              </div>
            </header>

            <div className="flex flex-wrap gap-2 mb-8">
              {video.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-background-100 text-foreground-600 text-xs px-3 py-1.5 rounded-full border border-background-200"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <p className="text-foreground-700 text-base leading-relaxed mb-8">
              {video.description}
            </p>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-semibold
                prose-p:leading-relaxed prose-p:my-3
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: video.detailContent }}
            />
          </div>
        </article>

        {relatedVideos.length > 0 && (
          <section className="py-10 bg-background-100">
            <div className="container-custom max-w-4xl">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedVideos.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}